import { useState } from "react";
import { Navigate } from "react-router";
import { errorMessage, platform } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Alert, Button, Field } from "../components/ui";

export default function Login() {
  const { authenticated, booting, login, signup } = useAuth();
  const [mode, setMode] = useState<"login" | "signup" | "reset">("login");
  const [resetSent, setResetSent] = useState(false);
  const [f, setF] = useState({ email: "", password: "", store_name: "", name: "", accept_terms: false });
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  if (authenticated) return <Navigate to="/" replace />;
  if (booting) return null; // încă verificăm dacă există o sesiune (cookie) înainte să afișăm formularul

  const set = (k: "email" | "password" | "store_name" | "name") => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      if (mode === "login") await login(f.email, f.password);
      else if (mode === "reset") {
        await platform.post("/auth/password-reset", { email: f.email });
        setResetSent(true);
      } else await signup(f);
    } catch (err) {
      setError(mode === "login" ? "Email sau parolă greșită." : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-indigo-50 to-white p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="text-sm font-bold uppercase tracking-[0.3em] text-brand">shopcore</div>
          <h1 className="mt-2 text-2xl font-bold">{mode === "login" ? "Intră în panou" : mode === "reset" ? "Ai uitat parola?" : "Deschide-ți magazinul"}</h1>
        </div>
        {mode === "reset" && resetSent ? (
          <div className="card space-y-4 p-6 text-sm">
            <p>Dacă există un cont cu adresa <b>{f.email}</b>, ți-am trimis un email cu un link pentru o parolă nouă. Linkul e valabil 2 ore.</p>
            <Button variant="secondary" className="w-full" onClick={() => { setMode("login"); setResetSent(false); }}>Înapoi la autentificare</Button>
          </div>
        ) : (
        <form onSubmit={submit} className="card space-y-4 p-6">
          {mode === "signup" && (
            <>
              <Field label="Numele magazinului"><input className="input" required value={f.store_name} onChange={set("store_name")} /></Field>
              <Field label="Numele tău"><input className="input" value={f.name} onChange={set("name")} /></Field>
            </>
          )}
          <Field label="Email"><input className="input" type="email" required autoComplete="email" value={f.email} onChange={set("email")} /></Field>
          {mode === "reset" ? (
            <p className="text-sm text-slate-500">Îți trimitem pe email un link ca să-ți alegi o parolă nouă.</p>
          ) : (
            <Field label="Parolă" hint={mode === "signup" ? "Minim 10 caractere" : undefined}>
              <input className="input" type="password" required autoComplete={mode === "login" ? "current-password" : "new-password"} value={f.password} onChange={set("password")} />
            </Field>
          )}
          {mode === "signup" && (
            <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-600">
              <input type="checkbox" required className="mt-0.5 h-4 w-4 accent-[var(--color-brand)]" checked={f.accept_terms}
                onChange={(e) => setF({ ...f, accept_terms: e.target.checked })} />
              <span>
                Am citit și accept <a href="/legal/terms" target="_blank" className="font-semibold text-brand underline">Termenii de utilizare</a> și{" "}
                <a href="/legal/dpa" target="_blank" className="font-semibold text-brand underline">Acordul de prelucrare a datelor</a>.
              </span>
            </label>
          )}
          {error && <Alert>{error}</Alert>}
          <Button className="w-full" disabled={busy}>
            {busy ? "Se încarcă…" : mode === "login" ? "Autentificare" : mode === "reset" ? "Trimite linkul" : "Creează magazinul"}
          </Button>
          {mode !== "signup" && (
            <button type="button" className="w-full text-center text-sm text-slate-500 hover:text-slate-800"
              onClick={() => { setMode(mode === "reset" ? "login" : "reset"); setError(undefined); }}>
              {mode === "reset" ? "Înapoi la autentificare" : "Am uitat parola"}
            </button>
          )}
        </form>
        )}
        {mode !== "reset" && <p className="mt-6 text-center text-sm text-slate-500">
          {mode === "login" ? "Nu ai magazin? " : "Ai deja cont? "}
          <button className="font-semibold text-brand" onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(undefined); }}>
            {mode === "login" ? "Creează unul gratuit" : "Autentifică-te"}
          </button>
        </p>}
      </div>
    </div>
  );
}
